import React, { forwardRef } from 'react';
import { STICKY_SECTION_HEADER_Z } from '../../constants/stickyTable';

/**
 * THE sticky-bottom horizontal scrollbar of a page-scroll table (🅱️ in
 * `constants/stickyTable.js`). Extracted 2026-09-26 from `OrderTableShell` when
 * the Breakdown and the Budget crew table took the same layout (owner: *"yh
 * this worked well do all other similar pages"*) — the Code Unification
 * Principle's second use.
 *
 * WHY IT EXISTS: in the page-scroll layout the rows pane is an x-only scroller
 * with no height cap, so its own scrollbar sits at the BOTTOM of the table —
 * below the fold on any long list — and the 2026-08-10 ruling *"THe horizontal
 * scroll bars iin table views on al pages should be sticky as we have to
 * scroll down the main page to find them"* still binds. The panes hide their
 * own bars (`PAGE_SCROLL_PANE_X`); this is the one the user drags. Its
 * `scrollLeft` is linked to the panes by `hooks/useLinkedScrollLeft` — pass the
 * forwarded ref (or mark it for a DOM-query link) at the CALLER.
 *
 * - `contentStyle` sizes the 1px spacer. Give it the SAME width expression the
 *   tables use (`w-full` + `minWidth`), so this bar's scroll range IS theirs
 *   with nothing to measure; a caller whose bar is wider than its panes (the
 *   Breakdown's bar spans the group cards' 1px borders) adds the difference.
 * - `stick` (default `true`) pins it at the viewport bottom: `bottom-[65px]`
 *   clears the fixed mobile bottom nav (`Layout.js`, `lg:hidden fixed
 *   bottom-0`), which is **65px, not 64**: an `h-16` row PLUS the nav's own
 *   1px `border-t` (measured in Chromium: nav 735–800 in an 800px viewport).
 *   `bottom-16` sat the bar 1px under the nav at 768–1023px (bar 725–736),
 *   so the nav covered its bottom pixel row — fixed 2026-09-27, the figure
 *   `PeopleManager`'s sticky bar already uses. `lg:bottom-0` above it (no
 *   nav). Pass `false` when the bar sits INSIDE a
 *   sticky block that already pins (the crew table's footer pane + bar).
 * - Render it ONLY while the content overflows — the caller decides; a short
 *   list gets no bar and no strip.
 * - 🪤 An `overflow-hidden` ANCESTOR is a scroll container, so the bar pins to
 *   IT, not the window (measured on /budget/overview); clip with `overflow-clip`.
 *
 * ♿ A duplicate control for mouse users: the rows pane is the real, keyboard-
 * and touch-reachable scroller, so this is hidden from assistive technology
 * and out of the tab order.
 *
 * 🔑 Rendered by `OrderTableShell` it was BYTE-IDENTICAL at extraction to the
 * inline element it replaced (class string, attributes, spacer); the one
 * deliberate change since is `bottom-16` → `bottom-[65px]` above, which moves
 * the order tables' bar too — it is the same bar.
 */
const StickyScrollbar = forwardRef(function StickyScrollbar(
  { contentStyle, testId, stick = true, ...rest },
  ref
) {
  const position = stick ? `sticky bottom-[65px] lg:bottom-0 ${STICKY_SECTION_HEADER_Z} ` : '';
  return (
    <div
      ref={ref}
      aria-hidden="true"
      tabIndex={-1}
      className={`${position}overflow-x-auto overflow-y-hidden bg-white dark:bg-gray-800`}
      data-testid={testId}
      // A drag here is the native scrollbar's, never a table pan (`useDragScroll`).
      data-pan-skip=""
      {...rest}
    >
      <div className="w-full h-px" style={contentStyle} />
    </div>
  );
});

export default StickyScrollbar;
